import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskText } from "@/components/pause/motion";
import { scrollToId } from "@/lib/smooth";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yDom = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const ySec = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-32 pb-16 mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col min-[900px]:flex-row items-center gap-10 min-[900px]:gap-16"
    >
      {/* Left — kinetic text */}
      <div className="w-full min-[900px]:w-1/2">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="font-body text-xs uppercase tracking-[0.28em] text-[#8C8070] mb-6"
        >
          Movement · Breathwork · Retreats
        </motion.p>

        <h1 className="font-display font-light text-[#1A1A1A] text-[19vw] min-[900px]:text-[8.5vw] leading-[0.86] tracking-[-0.02em]">
          <MaskText
            lines={[
              <span key="l1" className="italic text-[#3B5A3A]">Breathe in,</span>,
              <span key="l2">
                Pause<span className="text-[#F5C800]">.</span>
              </span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-md font-body text-base md:text-lg leading-relaxed text-[#8C8070]"
        >
          A gentle reset for body and mind. Personalized Viniyoga, intentional breath, and
          off-grid retreats with Nisha.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-join-cta"
            onClick={() => scrollToId("#waitlist")}
            className="font-body text-sm uppercase tracking-[0.12em] font-medium bg-[#F5C800] text-[#1A1A1A] px-7 py-3.5 rounded-full hover:scale-[1.04] hover:shadow-[0_10px_36px_rgba(245,200,0,0.35)] transition-transform duration-300"
          >
            Join the List
          </button>
          <button
            data-testid="hero-offerings-cta"
            onClick={() => scrollToId("#offerings")}
            className="font-body text-sm uppercase tracking-[0.12em] text-[#1A1A1A] px-7 py-3.5 rounded-full border border-[#1A1A1A]/25 hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF7F2] transition-colors duration-300"
          >
            Explore Offerings
          </button>
        </motion.div>
      </div>

      {/* Right — bento photo composition */}
      <div className="w-full min-[900px]:w-1/2 h-[62vh] min-[900px]:h-[78vh]">
        <div className="flex gap-3 h-full">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex-[1.35] overflow-hidden rounded-sm relative shadow-[0_24px_60px_rgba(26,26,26,0.14)]"
          >
            <motion.img
              style={{ y: yDom }}
              src="/images/photo1.jpg"
              alt="Nisha seated in meditation"
              className="w-full h-[114%] object-cover object-center"
            />
          </motion.div>

          <div className="flex-1 flex flex-col gap-3">
            <motion.div
              style={{ y: ySec }}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 overflow-hidden rounded-sm shadow-[0_18px_44px_rgba(26,26,26,0.12)]"
            >
              <img src="/images/photo4.jpg" alt="Seated stretch, arms raised" className="w-full h-full object-cover" />
            </motion.div>

            <motion.div
              style={{ y: ySec }}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.85, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 overflow-hidden rounded-sm shadow-[0_18px_44px_rgba(26,26,26,0.12)]"
            >
              <img src="/images/photo5.jpg" alt="Serene profile by the forest" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
